import * as React from 'react';

import { LucideCircleSlash } from 'lucide-react';

import { cn, isDefined, requiredLabelStyle } from '../..';

export type ContentCardProps = {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  isEmpty?: boolean;
  emptyText?: string;
  noXPadding?: boolean;
  style?: React.CSSProperties;
  flexHeight?: boolean;
  scrollable?: boolean;
};

export const ContentCard = (props: ContentCardProps) => {
  const hasStickyTitleChildren = React.useMemo(
    () =>
      React.Children.toArray(props.children).some(
        (child) =>
          React.isValidElement(child) &&
          child.type === ContentSection &&
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (child.props as any).stickyTitle,
      ),
    [props.children],
  );

  return (
    <div
      className={cn(
        `@ContentCard overflow-x-auto rounded-lg border border-neutral/10 bg-white p-5 pt-4 text-sm`,
        props.noXPadding && 'px-0',
        hasStickyTitleChildren && 'gap-0 p-0',
        props.flexHeight && 'h-0 grow',
        props.scrollable && 'no-scrollbar overflow-auto',
        props.className,
      )}
      onClick={props.onClick}
      style={props.style}
      data-sticky-title={hasStickyTitleChildren}
    >
      {props.children}
    </div>
  );
};

export type ContentSectionProps = {
  className?: string;
  titleClassName?: string;
  children: React.ReactNode;
  title?: React.ReactNode;
  required?: boolean;
  stickyTitle?: boolean;
  variant?: 'default' | 'panel';
  hideDivider?: boolean;
};

export const ContentSection = (props: ContentSectionProps) => {
  const { variant = 'default' } = props;
  const isPanel = variant === 'panel';

  return (
    <div
      className={cn(
        '@ContentSection flex flex-col',
        !isPanel &&
          'gap-3 not-first:border-t not-first:border-neutral/10 not-first:pt-5 not-last:mb-5',
        isPanel && 'mx-5 mb-6 gap-0 border-b border-neutral/10 pb-6',
        isPanel && props.hideDivider && 'border-b-0 pb-0',
        props.stickyTitle && 'gap-0',
        props.className,
      )}
    >
      {props.title && (
        <div
          aria-required={props.required}
          className={cn(
            !isPanel && 'text-base font-light text-neutral/40 capitalize',
            isPanel && 'pb-4 text-lg font-semibold text-neutral/90',
            props.stickyTitle && 'sticky top-0 z-10 bg-white p-5',
            !isPanel && requiredLabelStyle,
            props.titleClassName,
          )}
        >
          {props.title}
          {props.required && !isPanel && (
            <>
              {' '}
              <span className="text-failure">*</span>
            </>
          )}
        </div>
      )}
      {props.stickyTitle ? (
        <div className="flex h-full flex-col gap-3 p-5 pt-0">
          {props.children}
        </div>
      ) : (
        props.children
      )}
    </div>
  );
};

export type ContentInfoProps<T> = {
  className?: string;
  label: React.ReactNode;
  value: T | undefined;
  children: (value: T) => React.ReactNode;
  colspan?: number;
};

export const ContentInfo = <T,>(props: ContentInfoProps<T>) => (
  <div
    className={cn(`@ContentInfo flex flex-col gap-1`, props.className)}
    style={{ gridColumn: props.colspan ? `span ${props.colspan}` : undefined }}
  >
    <div className="text-xs font-medium text-neutral/60">{props.label}</div>
    <div className="flex min-h-[32px] items-center text-sm">
      {isDefined(props.value) ? props.children(props.value) || '–' : '–'}
    </div>
  </div>
);

export type ContentEmptyProps = { children: React.ReactNode };

export const ContentEmpty = (props: ContentEmptyProps) => (
  <ContentCard className="@ContentEmpty p-20">
    <div className="flex w-full flex-col items-center justify-center gap-4 text-sm">
      <LucideCircleSlash size={48} className="opacity-30" />
      <span>{props.children}</span>
    </div>
  </ContentCard>
);
